/**
 * Single source of truth for the Create-Item "Framing spec" dropdowns.
 *
 * ⚠️ The `value` of every option below is written verbatim into `itemJson` and
 * read by the Master List summary engine (`woItemToken`). These values MUST stay
 * identical to the WO_*_CODE map keys in:
 *   - src/components/MasterlistTable/MasterlistTable.tsx   (frontend engine copy)
 *   - tpw-server-functions … deriveWorklistCheckFields.ts (backend engine copy)
 * Change a value here → change the matching map key in BOTH engine copies.
 * Labels are staff-facing only; codes never appear in the form.
 */

export type Opt = { value: string; label: string };
export type OptGroup = { group: string; items: Opt[] };

export const SUPPLIED_MEDIA_VALUE = "__supplied";
export const CUSTOM_VALUE = "__custom";

/** Media → paperOptions.paper (or the supplied sentinel). */
export const MEDIA_OPTIONS: Opt[] = [
  { value: SUPPLIED_MEDIA_VALUE, label: "Customer supplied" },
  { value: "Hahnemuhle German Etching", label: "Hahnemühle German Etching" },
  { value: "Hahnemuhle Photo Rag Bright", label: "Hahnemühle Photo Rag Bright" },
  { value: "Hahnemuhle Photo Rag 308", label: "Hahnemühle Photo Rag 308" },
  { value: "Hahnemuhle Fine Art Pearl", label: "Hahnemühle Fine Art Pearl" },
  { value: "Hahnemuhle Photo Rag Baryta", label: "Hahnemühle Photo Rag Baryta" },
  { value: "Hahnemuhle Photo Rag Metallic", label: "Hahnemühle Photo Rag Metallic" },
  { value: "Hahnemuhle Photo Rag Ultra Smooth", label: "Hahnemühle Photo Rag Ultra Smooth" },
  { value: "Epson Hot Press Bright", label: "Epson Hot Press Bright" },
  { value: "Epson Cold Press Bright", label: "Epson Cold Press Bright" },
  { value: "Epson Enhanced Matte", label: "Epson Enhanced Matte" },
  { value: "Premium Luster Photo Paper", label: "Premium Luster Photo" },
  { value: "Premium Glossy Photo Paper", label: "Premium Glossy Photo" },
  { value: "Epson Glossy Metallic", label: "Epson Metallic Photo" },
  { value: "Exhibition Matte Canvas", label: "Exhibition Matte Canvas" },
  { value: "Metallic Canvas", label: "Silverada Canvas" },
];

/**
 * Product type → itemJson.productType. This is the single "what is this item"
 * selector; Category is auto-derived from it (see PRODUCT_TYPE_TO_CATEGORY).
 * Framing/print types carry the framing spec + a coded summary; service types
 * (Scan/Mount/Shipping) skip the spec and emit a plain summary label.
 * NOTE: "Giclée" is a display label only — the value stays "Fine Art Print" so
 * the summary engine and DB/exports are unchanged.
 */
export const PRODUCT_TYPE_OPTIONS: Opt[] = [
  { value: "Framed Print", label: "Framed Print" },
  { value: "Fine Art Print", label: "Giclée" },
  { value: "Canvas", label: "Canvas" },
  { value: "HD Aluminum", label: "HD Aluminum" },
  { value: "Scan", label: "Scan / Proof / Retouch" },
  { value: "Shipping", label: "Shipping" },
];

/** Product types that carry a framing spec + a coded Master List token. */
export const FRAMING_PRODUCT_TYPES = new Set([
  "Framed Print",
  "Fine Art Print",
  "Canvas",
  "HD Aluminum",
]);

/** Service/other product types — no framing spec; summary shows a plain label. */
export const SERVICE_PRODUCT_TYPES = new Set(["Scan", "Mounting", "Shipping"]);

/** Product type → legacy DB Category (auto-derived; the Category field is hidden). */
export const PRODUCT_TYPE_TO_CATEGORY: Record<string, string> = {
  "Framed Print": "Framing",
  "Fine Art Print": "Art Print",
  Canvas: "Canvas",
  "HD Aluminum": "Aluminum",
  Scan: "Scan",
  Mounting: "Mount",
  Shipping: "Shipping",
};

/** Staff-facing top "Custom Order" category (the single selector). Each maps to a
 *  productType (+ treatment) in LineItemForm; these are labels only, never stored. */
export const CUSTOM_ORDER_OPTIONS: Opt[] = [
  { value: "printing", label: "Printing" },
  { value: "framing", label: "Framing" },
  { value: "mounting", label: "Mounting" },
  { value: "scan", label: "Scan / Proof / Retouch" },
  { value: "shipping", label: "Shipping" },
];

/** Under "Printing": substrate choice -> itemJson.productType (bare, unframed output). */
export const PRINT_ON_OPTIONS: Opt[] = [
  { value: "Fine Art Print", label: "Paper (giclée)" },
  { value: "Canvas", label: "Canvas" },
  { value: "HD Aluminum", label: "HD Aluminum" },
];

/**
 * Treatment → treatmentOptions.type. Engine substring-matches these (lowercased):
 * "float" → "float mnt"; "shadow"/"exhibition" → "set back" (deep); "mat only" →
 * mat-only; "print only" → no frame; "custom" → "custom — see invoice". A shallow
 * SF / M&F piece sits flush ("in <FRAME>") unless frameOptions.setBack is ticked.
 */
export const TREATMENT_OPTIONS: Opt[] = [
  { value: "Straight Fit", label: "Straight Fit" },
  { value: "Matted", label: "Matted & Framed" },
  { value: "Shadow Box", label: "Shadow Box" },
  { value: "Exhibition Shadow Box", label: "Exhibition Shadow Box" },
  { value: "Float Mount", label: "Float Mount" },
  { value: "Mat Only", label: "Mat only (no frame)" },
  { value: "Print Only", label: "Print only (unframed)" },
  { value: "Mounted (on substrate)", label: "Mounted (on substrate)" },
  { value: "Custom", label: "Custom (see invoice)" },
];

/** Frame / moulding → frameOptions.label. Groups are cosmetic (UI only). */
export const FRAME_GROUPS: OptGroup[] = [
  {
    group: "Gallery (shallow)",
    items: [
      { value: "Black Gallery", label: "Black Gallery Frame" },
      { value: "White Gallery", label: "White Gallery Frame" },
      { value: "Maple Gallery", label: "Maple Gallery Frame" },
      { value: "Gold Gallery", label: "Gold Gallery Frame" },
      { value: "Silver Gallery", label: "Silver Gallery Frame" },
    ],
  },
  {
    group: "Thin",
    items: [
      { value: "Thin Black", label: "Thin Black" },
      { value: "Thin White", label: "Thin White" },
      { value: "Thin Maple", label: "Thin Maple" },
    ],
  },
  {
    group: "Shadow Box (deep)",
    items: [
      { value: "Black Shadow Box", label: "Black Shadow Box" },
      { value: "White Shadow Box", label: "White Shadow Box" },
      { value: "Maple Shadow Box", label: "Maple Shadow Box" },
      { value: "Gold Shadow Box", label: "Gold Shadow Box" },
      { value: "Silver Shadow Box", label: "Silver Shadow Box" },
      { value: "Dark Walnut", label: "Dark Walnut" },
    ],
  },
  {
    group: "Deep Shadow Box (oversize / structural)",
    items: [
      { value: "Deep Black Shadow Box", label: "Deep Black Shadow Box" },
      { value: "Deep White Shadow Box", label: "Deep White Shadow Box" },
      { value: "Deep Weathered Walnut", label: "Deep Weathered Walnut" },
      { value: "Deep Weathered Black", label: "Deep Weathered Black" },
      { value: "Deep Weathered Gray", label: "Deep Weathered Gray" },
      { value: "Deep Weathered White", label: "Deep Weathered White" },
    ],
  },
  {
    group: "Weathered",
    items: [
      { value: "Weathered White", label: "Weathered White" },
      { value: "Weathered Black", label: "Weathered Black" },
      { value: "Weathered Gray", label: "Weathered Gray" },
      { value: "Weathered Walnut", label: "Weathered Walnut" },
    ],
  },
  {
    group: "Floater frame (canvas / panel)",
    items: [
      { value: "Black Floater Frame", label: "Black Floater Frame" },
      { value: "White Floater Frame", label: "White Floater Frame" },
      { value: "Gold Floater Frame", label: "Gold Floater Frame" },
      { value: "Silver Floater Frame", label: "Silver Floater Frame" },
      { value: "Weathered Walnut Floater Frame", label: "Weathered Walnut Floater" },
      { value: "Weathered Gray Floater Frame", label: "Weathered Gray Floater" },
      { value: "Weathered Black Floater Frame", label: "Weathered Black Floater" },
      { value: "Weathered White Floater Frame", label: "Weathered White Floater" },
      { value: "Dark Walnut Floater Frame", label: "Dark Walnut Floater" },
    ],
  },
  {
    group: "Other",
    items: [
      { value: CUSTOM_VALUE, label: "Custom moulding…" },
      { value: "", label: "(none)" },
    ],
  },
];

/** Acrylic / glazing → frameOptions.glass.type. */
export const ACRYLIC_OPTIONS: Opt[] = [
  { value: "Regular Acrylic", label: "Regular Acrylic" },
  { value: "Nonglare Acrylic", label: "Non-Glare Acrylic" },
  { value: "UV-Resistant Acrylic OP3", label: "UV-Resistant (OP3)" },
  { value: "Museum Acrylic", label: "Museum Acrylic" },
  { value: "", label: "(none)" },
];

/** Mat + backing colour codes → matboard.color / backing.color. */
// The visualizer's seven (framingLabels MAT_COLORS). The core and Loam values
// are the full names so the Cut Batch colour codes resolve (cutList COLOR_CODE).
export const MAT_COLOR_OPTIONS: Opt[] = [
  { value: "", label: "—" },
  { value: "W", label: "White" },
  { value: "AntW", label: "Antique White" },
  { value: "White with Black Core", label: "White with Black Core" },
  { value: "Black", label: "Black" },
  { value: "Black with White Core", label: "Black with White Core" },
  { value: "Cloud Gray", label: "Cloud Gray" },
  { value: "Loam", label: "Loam" },
  { value: CUSTOM_VALUE, label: "Custom matboard…" },
];

export const BACKING_COLOR_OPTIONS: Opt[] = [
  { value: "", label: "—" },
  { value: "AntW", label: "Antique White" },
  { value: "W", label: "White" },
  { value: "Black", label: "Black" },
  { value: CUSTOM_VALUE, label: "Custom backing…" },
];

export const MAT_PLY_OPTIONS: Opt[] = [
  { value: "", label: "—" },
  { value: "4-ply", label: "4-ply" },
  { value: "8-ply", label: "8-ply" },
];

/** ── Canvas-specific vocab (Create-Item) ─────────────────────────────────────
 * Canvas is described differently from a giclée/framed print: construction +
 * canvas media + a wrap edge (stretched/floater) or printed border (loose),
 * optionally a floater frame. Values are written into itemJson and read by the
 * Master List engine (woItemToken) the same way the framing vocab above is.
 */

/** Construction → treatmentOptions.type (mirrors the app's CANVAS_TREATMENTS + Loose). */
export const CANVAS_CONSTRUCTION_OPTIONS: Opt[] = [
  { value: "Canvas on Stretcher", label: "Stretched Canvas" },
  { value: "Loose Canvas", label: "Loose Canvas" },
  { value: "Canvas in Floater Frame", label: "Canvas in Floater Frame" },
  { value: "Mounted (on substrate)", label: "Mounted (on substrate)" },
];

/** Canvas substrates → paperOptions.paper (only two). */
export const CANVAS_MEDIA_OPTIONS: Opt[] = [
  { value: SUPPLIED_MEDIA_VALUE, label: "Customer supplied" },
  { value: "Exhibition Matte Canvas", label: "Exhibition Matte Canvas" },
  { value: "Metallic Canvas", label: "Silverada Canvas" },
];

/** Edge/border. Stretched/floater = wrap edge; loose = printed border (carries a
 *  size) or full bleed. Maps to paperOptions.mirrored / border. */
export const CANVAS_EDGE_OPTIONS: Opt[] = [
  { value: "mirror", label: "Mirror image" },
  { value: "border", label: "Border color" },
  { value: "full-bleed", label: "Full bleed" },
];

/** Border color (when edge = border) → paperOptions.borderColor. */
export const CANVAS_BORDER_COLOR_OPTIONS: Opt[] = [
  { value: "#ffffff", label: "White" },
  { value: "#000000", label: "Black" },
  { value: CUSTOM_VALUE, label: "Custom…" },
];

/** HD Aluminum finish → itemJson.type (engine reads WO_HDAP_FINISH). */
export const HD_ALUMINUM_FINISH_OPTIONS: Opt[] = [
  { value: "Glossy White Finish", label: "Glossy White" },
  { value: "Matte White Finish", label: "Matte White" },
  { value: "Glossy Silver Finish", label: "Glossy Silver" },
  { value: "Matte Silver Finish", label: "Matte Silver" },
];

/** HD Aluminum mount → treatmentOptions.type. Floater adds a frame code. */
export const HD_ALUMINUM_MOUNT_OPTIONS: Opt[] = [
  { value: "Panel (no frame)", label: "Panel (no frame)" },
  { value: "Aluminum in Floater Frame", label: "Aluminum in Floater Frame" },
];

/** Mounting substrate → itemJson.mountSubstrate (value IS the Master List code). */
export const MOUNT_SUBSTRATE_OPTIONS: Opt[] = [
  { value: "3/16 FB", label: '3/16" Foam Board' },
  { value: "1/8 FB", label: '1/8" Foam Board' },
  { value: "sntra", label: "Sintra" },
  { value: "dbond", label: "Dibond" },
];

/** Framed treatments that mean a physical frame is built (drives usesFrame). */
export const FRAMED_TREATMENTS = new Set([
  "Straight Fit",
  "Matted",
  "Shadow Box",
  "Exhibition Shadow Box",
  "Float Mount",
]);

/**
 * Physical frame face width (inches) per moulding, from framingLabels'
 * `dimensions.face`. Used only for the display-only overall-size estimate.
 * Custom mouldings aren't here (unknown) → overall shows blank.
 */
export const FRAME_FACE: Record<string, number> = {
  "Black Gallery": 0.75,
  "White Gallery": 0.75,
  "Maple Gallery": 0.75,
  "Gold Gallery": 0.75,
  "Silver Gallery": 0.75,
  "Thin Black": 0.5,
  "Thin White": 0.5,
  "Thin Maple": 0.5,
  "Black Shadow Box": 1,
  "White Shadow Box": 1,
  "Maple Shadow Box": 1,
  "Gold Shadow Box": 1,
  "Silver Shadow Box": 1,
  "Dark Walnut": 0.75,
  "Weathered White": 0.75,
  "Weathered Black": 0.75,
  "Weathered Gray": 0.75,
  "Weathered Walnut": 0.75,
  "Deep Black Shadow Box": 1,
  "Deep White Shadow Box": 1,
  "Deep Weathered Walnut": 1.25,
  "Deep Weathered Gray": 1.25,
  "Deep Weathered Black": 1.25,
  "Deep Weathered White": 1.25,
  "Black Floater Frame": 0.75,
  "White Floater Frame": 0.75,
  "Gold Floater Frame": 0.75,
  "Silver Floater Frame": 0.75,
  "Weathered Walnut Floater Frame": 0.75,
  "Weathered Gray Floater Frame": 0.75,
  "Weathered Black Floater Frame": 0.75,
  "Weathered White Floater Frame": 0.75,
  "Dark Walnut Floater Frame": 0.75,
};

/**
 * Display-only overall (outer) size derived from the framing spec — mirrors the
 * visualizer's overall-dimension math (image + mat×2 + reveal×2 + face×2 − 0.5;
 * floats keep the 0.5). Returns null when it can't be known (non-framing item,
 * missing size, or a custom moulding with no face on file).
 */
export function computeOverallSize(spec: {
  productType: string;
  width: number;
  height: number;
  treatment: string;
  frameLabel: string;
  matWidth: string;
  reveal: string;
}): { width: number; height: number } | null {
  if (!FRAMING_PRODUCT_TYPES.has(spec.productType)) return null;
  const w = Number(spec.width) || 0;
  const h = Number(spec.height) || 0;
  if (!w || !h) return null;
  const tr = (spec.treatment || "").toLowerCase();
  const mat = (Number(spec.matWidth) || 0) * 2;
  const rev = (Number(spec.reveal) || 0) * 2;
  const round = (n: number) => Math.round(n * 100) / 100;
  if (tr.includes("print only")) return { width: round(w), height: round(h) };
  if (tr.includes("mat only") || tr.includes("mat-only"))
    return { width: round(w + mat), height: round(h + mat) };
  if (!spec.frameLabel || spec.frameLabel === CUSTOM_VALUE) return null;
  const face = FRAME_FACE[spec.frameLabel];
  if (face == null) return null;
  const trim = tr.includes("float") ? 0 : 0.5;
  const add = mat + rev + face * 2 - trim;
  return { width: round(w + add), height: round(h + add) };
}

// ── Customer-facing breakdown ────────────────────────────────────────────────
// The visualizer describes an order to the customer by writing plain-English
// strings into cartData.staticField2/3/4 + conditionalFields (see
// stores/actions/createLineItem.ts). Every customer surface — the on-screen cart
// (CartLineItem), the invoice / packing-slip PDF (CartPdf), profile + dashboard
// order views — reads those same fields. Create Item captures the structured
// spec but left those display fields blank, so a custom item showed only its
// Name + price. deriveCustomerFacingFields() makes Create Item a second producer
// of that display shape, so a custom order reads like a normal order everywhere.
// ⚠ Customer-facing ONLY — NEVER emit an internal code (DKWal, suppl'd, reg acr,
// etc.) here. Those live in the Master List woItemToken engine, staff-side.

/** All frame options flattened for value→label lookup. */
const FRAME_OPTIONS_FLAT: Opt[] = FRAME_GROUPS.flatMap(g => g.items);

/** Return an option's staff-facing label, falling back to the raw value. */
function labelForValue(opts: Opt[], value: string): string {
  const hit = opts.find(o => o.value === value);
  return hit ? hit.label : value;
}

export type CustomerFacingFields = {
  staticField2: string;
  staticField3: string;
  staticField4: string;
  conditionalFields: string[];
};

/**
 * Build the plain-English cart/invoice breakdown from the Create-Item framing
 * spec, mirroring the visualizer's staticField + conditionalFields shape:
 *   staticField2 = size · staticField3 = media / "Customer supplied" ·
 *   staticField4 = treatment · conditionalFields = frame, mat, reveal, backing, glazing.
 * Service/non-framing product types return all-empty (Name + price carry them).
 */
export function deriveCustomerFacingFields(
  spec: {
    productType: string;
    media: string;
    suppliedMatted: boolean;
    treatment: string;
    frameLabel: string;
    customMoulding: string;
    acrylic: string;
    matWidth: string;
    matPly: string;
    matColor: string;
    customMat: string;
    reveal: string;
    backingColor: string;
    customBacking: string;
    // Canvas (optional; set by the Create-Item canvas flow):
    edgeType?: string;
    borderColor?: string;
    customBorderColor?: string;
    borderSize?: string;
    finish?: string;
    deckle?: boolean;
    substrate?: string;
    brace?: boolean;
    cleat?: boolean;
  },
  width: number,
  height: number
): CustomerFacingFields {
  const empty: CustomerFacingFields = {
    staticField2: "",
    staticField3: "",
    staticField4: "",
    conditionalFields: [],
  };
  if (!FRAMING_PRODUCT_TYPES.has(spec.productType)) return empty;

  const dim = (n: number) => String(Math.round(n * 1000) / 1000);

  // Mounted (on substrate) — substrate + optional floater + brace + cleat.
  if (/mount/i.test(spec.treatment) && !/float/i.test(spec.treatment)) {
    const mCond: string[] = [];
    const subLabel = spec.substrate
      ? labelForValue(MOUNT_SUBSTRATE_OPTIONS, spec.substrate)
      : "";
    if (subLabel) mCond.push(`Mounted on ${subLabel}`);
    if (spec.frameLabel) {
      let frameText =
        spec.frameLabel === CUSTOM_VALUE
          ? spec.customMoulding.trim()
          : labelForValue(FRAME_OPTIONS_FLAT, spec.frameLabel);
      if (frameText) {
        if (!/frame|moulding|floater/i.test(frameText)) frameText += " Frame";
        mCond.push(frameText);
      }
    }
    if (spec.brace) mCond.push("Wood brace");
    if (spec.cleat) mCond.push("French cleat");
    const mSupplied = spec.media === SUPPLIED_MEDIA_VALUE;
    return {
      staticField2: width && height ? `${dim(width)}" × ${dim(height)}"` : "",
      staticField3: mSupplied
        ? "Customer supplied"
        : spec.media
          ? labelForValue(MEDIA_OPTIONS, spec.media)
          : "",
      staticField4: "Mounted",
      conditionalFields: mCond,
    };
  }

  // Canvas — construction + media + edge/border (+ floater). No mat/glazing/
  // reveal; the wrap edge or printed border rides in conditionalFields.
  if (spec.productType === "Canvas") {
    const cCond: string[] = [];
    if (spec.frameLabel) {
      let frameText =
        spec.frameLabel === CUSTOM_VALUE
          ? spec.customMoulding.trim()
          : labelForValue(FRAME_OPTIONS_FLAT, spec.frameLabel);
      if (frameText) {
        if (!/frame|moulding|floater/i.test(frameText)) frameText += " Frame";
        cCond.push(frameText);
      }
    }
    const isLoose = /loose/i.test(spec.treatment || "");
    if (spec.edgeType === "mirror") {
      cCond.push("Mirrored edge");
    } else if (spec.edgeType === "border") {
      const colorText =
        spec.borderColor === CUSTOM_VALUE
          ? (spec.customBorderColor || "").trim()
          : spec.borderColor
            ? labelForValue(CANVAS_BORDER_COLOR_OPTIONS, spec.borderColor)
            : "";
      const sizeText = isLoose && spec.borderSize ? `${spec.borderSize}" ` : "";
      const border = `${sizeText}${colorText} border`.replace(/\s+/g, " ").trim();
      if (border && border !== "border") cCond.push(border);
    } else if (spec.edgeType === "full-bleed") {
      cCond.push("Full bleed");
    }
    return {
      staticField2: width && height ? `${dim(width)}" × ${dim(height)}"` : "",
      staticField3: spec.media
        ? labelForValue(CANVAS_MEDIA_OPTIONS, spec.media)
        : "",
      staticField4: spec.treatment
        ? labelForValue(CANVAS_CONSTRUCTION_OPTIONS, spec.treatment)
        : "",
      conditionalFields: cCond,
    };
  }

  // HD Aluminum — finish + mount (+ floater). No paper / mat / glazing.
  if (spec.productType === "HD Aluminum") {
    const cCond: string[] = [];
    if (spec.frameLabel) {
      let frameText =
        spec.frameLabel === CUSTOM_VALUE
          ? spec.customMoulding.trim()
          : labelForValue(FRAME_OPTIONS_FLAT, spec.frameLabel);
      if (frameText) {
        if (!/frame|moulding|floater/i.test(frameText)) frameText += " Frame";
        cCond.push(frameText);
      }
    }
    return {
      staticField2: width && height ? `${dim(width)}" × ${dim(height)}"` : "",
      staticField3: spec.finish
        ? labelForValue(HD_ALUMINUM_FINISH_OPTIONS, spec.finish)
        : "",
      staticField4: spec.treatment
        ? labelForValue(HD_ALUMINUM_MOUNT_OPTIONS, spec.treatment)
        : "",
      conditionalFields: cCond,
    };
  }

  const supplied = spec.media === SUPPLIED_MEDIA_VALUE;

  // Size
  const staticField2 = width && height ? `${dim(width)}" × ${dim(height)}"` : "";

  // Media / supplied
  let staticField3 = "";
  if (supplied) {
    staticField3 = spec.suppliedMatted
      ? "Customer supplied (matted)"
      : "Customer supplied";
  } else if (spec.media) {
    staticField3 = labelForValue(MEDIA_OPTIONS, spec.media);
  }

  // Treatment
  const staticField4 = spec.treatment
    ? labelForValue(TREATMENT_OPTIONS, spec.treatment)
    : "";

  const conditionalFields: string[] = [];
  if (spec.deckle) conditionalFields.push("Deckle edge");

  // Frame / moulding
  if (spec.frameLabel) {
    let frameText =
      spec.frameLabel === CUSTOM_VALUE
        ? spec.customMoulding.trim()
        : labelForValue(FRAME_OPTIONS_FLAT, spec.frameLabel);
    if (frameText) {
      if (!/frame|moulding|floater/i.test(frameText)) frameText += " Frame";
      conditionalFields.push(frameText);
    }
  }

  // Mat (width + ply + color)
  const matW = parseFloat(spec.matWidth);
  const matColorText =
    spec.matColor === CUSTOM_VALUE
      ? spec.customMat.trim()
      : spec.matColor
        ? labelForValue(MAT_COLOR_OPTIONS, spec.matColor)
        : "";
  if (matColorText || matW > 0) {
    const matLine = [
      matW > 0 ? `${matW}"` : "",
      spec.matPly || "",
      matColorText,
    ]
      .filter(Boolean)
      .join(" ");
    if (matLine) conditionalFields.push(`${matLine} Mat`);
  }

  // Reveal (floats)
  const revealNum = parseFloat(spec.reveal);
  if (revealNum > 0) conditionalFields.push(`${revealNum}" Reveal`);

  // Backing (floats)
  const backingText =
    spec.backingColor === CUSTOM_VALUE
      ? spec.customBacking.trim()
      : spec.backingColor
        ? labelForValue(BACKING_COLOR_OPTIONS, spec.backingColor)
        : "";
  if (backingText) conditionalFields.push(`${backingText} Backing`);

  // Glazing
  if (spec.acrylic) {
    conditionalFields.push(labelForValue(ACRYLIC_OPTIONS, spec.acrylic));
  }

  return { staticField2, staticField3, staticField4, conditionalFields };
}
